import { useEffect, useRef, useState } from "react";
import api, { API } from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { Field } from "./BankAccountsPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Trash2, Pencil, Download, Upload } from "lucide-react";
import { refreshPartnerCache } from "../components/PartnerAutocomplete";

export default function PartnersPage() {
    const { hasPerm } = useAuth();
    const canWrite = hasPerm("partners", "write");
    const [rows, setRows] = useState([]);
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(blank());
    const [editId, setEditId] = useState(null);

    function blank() {
        return {
            name: "", type: "both",
            address_line1: "", address_line2: "", city: "", postcode: "", country: "",
            vat_number: "", phone: "",
            emails_text: "",
            purchase_cycle: "monthly",
            month_end_cut: false,
        };
    }

    const startEdit = (p) => {
        setEditId(p.id);
        setForm({
            name: p.name || "",
            type: p.type || "both",
            address_line1: p.address_line1 || "",
            address_line2: p.address_line2 || "",
            city: p.city || "",
            postcode: p.postcode || "",
            country: p.country || "",
            vat_number: p.vat_number || "",
            phone: p.phone || "",
            emails_text: (p.emails || []).join(", "),
            purchase_cycle: p.purchase_cycle || "monthly",
            month_end_cut: !!p.month_end_cut,
        });
        setOpen(true);
    };

    const closeDialog = (v) => {
        setOpen(v);
        if (!v) { setEditId(null); setForm(blank()); }
    };

    const load = async () => {
        const { data } = await api.get("/partners");
        setRows(data);
    };
    useEffect(() => {
        load();
    }, []);

    const submit = async () => {
        if (!form.name) return toast.error("Name required");
        const emails = (form.emails_text || "")
            .split(/[\s,;\n]+/)
            .map((e) => e.trim())
            .filter(Boolean);
        const invalid = emails.filter((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
        if (invalid.length) {
            return toast.error(`Invalid email(s): ${invalid.join(", ")}`);
        }
        try {
            const { emails_text: _omit, ...rest } = form;
            const body = { ...rest, emails };
            if (editId) {
                await api.put(`/partners/${editId}`, body);
                toast.success("Partner updated");
            } else {
                await api.post("/partners", body);
                toast.success("Partner created");
            }
            closeDialog(false);
            await load();
            // The voucher pages cache the partner list at module level — bust
            // it so newly-created/renamed partners appear in the dropdown
            // without requiring a hard refresh.
            await refreshPartnerCache();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete partner?")) return;
        await api.delete(`/partners/${id}`);
        await load();
        await refreshPartnerCache();
    };

    const filtered = rows.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()));

    const fileRef = useRef(null);
    const downloadTemplate = async () => {
        const token = localStorage.getItem("erp_access_token");
        const res = await fetch(`${API}/partners/template`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "partners_template.csv";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const onUploadFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/partners/upload", fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            const skipped = (data.skipped || []).length;
            toast.success(`Imported — ${data.inserted} new, ${data.updated} updated${skipped ? `, ${skipped} skipped` : ""}`);
            if (skipped) {
                console.warn("Partner import skipped rows:", data.skipped);
                toast.warning(`Skipped: ${data.skipped.slice(0, 3).join("; ")}${data.skipped.length > 3 ? "…" : ""}`);
            }
            await load();
            await refreshPartnerCache();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            e.target.value = "";
        }
    };

    return (
        <div data-testid="partners-page">
            <PageHeader
                title="Partners"
                eyebrow="Masters"
                description="Customers and vendors used across invoices, receipts, payments and credit notes."
                action={
                    canWrite ? (
                        <div className="flex flex-wrap items-center gap-2">
                            <Button variant="outline" size="sm" onClick={downloadTemplate}
                                    className="rounded-sm h-9" data-testid="partners-template-btn">
                                <Download size={13} className="mr-1.5"/>Template (.csv)
                            </Button>
                            <input ref={fileRef} type="file" accept=".csv" onChange={onUploadFile} hidden data-testid="partners-import-file"/>
                            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}
                                    className="rounded-sm h-9" data-testid="partners-import-btn">
                                <Upload size={13} className="mr-1.5"/>Bulk import
                            </Button>
                            <Dialog open={open} onOpenChange={closeDialog}>
                                <DialogTrigger asChild>
                                    <Button className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]" data-testid="partner-add-btn">
                                        + New partner
                                    </Button>
                                </DialogTrigger>
                            <DialogContent className="rounded-sm max-w-xl">
                                <DialogHeader>
                                    <DialogTitle>{editId ? "Edit partner" : "Create partner"}</DialogTitle>
                                </DialogHeader>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Field label="Partner name">
                                        <Input
                                            value={form.name}
                                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            className="rounded-sm h-9"
                                            data-testid="partner-name-input"
                                        />
                                    </Field>
                                    <Field label="Type">
                                        <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                                            <SelectTrigger className="rounded-sm" data-testid="partner-type-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="customer">Customer</SelectItem>
                                                <SelectItem value="vendor">Vendor</SelectItem>
                                                <SelectItem value="both">Both</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Address line 1">
                                        <Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} className="rounded-sm h-9" data-testid="partner-addr1-input"/>
                                    </Field>
                                    <Field label="Address line 2">
                                        <Input value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} className="rounded-sm h-9" data-testid="partner-addr2-input"/>
                                    </Field>
                                    <Field label="City">
                                        <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-sm h-9" data-testid="partner-city-input"/>
                                    </Field>
                                    <Field label="Postcode">
                                        <Input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} className="rounded-sm h-9" data-testid="partner-postcode-input"/>
                                    </Field>
                                    <Field label="Country">
                                        <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="rounded-sm h-9" data-testid="partner-country-input"/>
                                    </Field>
                                    <Field label="VAT #">
                                        <Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="partner-vat-input"/>
                                    </Field>
                                    <Field label="Phone">
                                        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="partner-phone-input"/>
                                    </Field>
                                    <Field label="Purchase reconciliation cycle">
                                        <Select value={form.purchase_cycle} onValueChange={(v) => setForm({ ...form, purchase_cycle: v })}>
                                            <SelectTrigger className="rounded-sm" data-testid="partner-purchase-cycle"><SelectValue/></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="weekly">Weekly (Mon–Sun)</SelectItem>
                                                <SelectItem value="fortnightly">Fortnightly (1–15, 16–EOM)</SelectItem>
                                                <SelectItem value="monthly">Monthly</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Month-end cut">
                                        <label className="flex items-center gap-2 text-[12.5px] text-slate-700 h-9 px-3 border border-slate-200 rounded-sm bg-white cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={!!form.month_end_cut}
                                                onChange={(e) => setForm({ ...form, month_end_cut: e.target.checked })}
                                                disabled={form.purchase_cycle === "monthly"}
                                                data-testid="partner-month-end-cut"
                                                className="accent-[#002FA7]"
                                            />
                                            <span>Split cycles at month boundary {form.purchase_cycle === "monthly" ? "(N/A for monthly)" : "(vendor issues 2 invoices around month-end)"}</span>
                                        </label>
                                    </Field>
                                    <div className="col-span-2">
                                        <Field label="Invoice email recipients">
                                            <Input
                                                value={form.emails_text}
                                                onChange={(e) => setForm({ ...form, emails_text: e.target.value })}
                                                className="rounded-sm h-9 font-mono text-[12px]"
                                                placeholder="ar@vonip.co.uk, accounts@vonip.co.uk"
                                                data-testid="partner-emails-input"
                                            />
                                        </Field>
                                        <div className="text-[11px] text-slate-500 mt-1">
                                            Comma, semicolon or newline separated. Used when emailing invoices to this partner (email integration coming soon).
                                        </div>
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button variant="ghost" onClick={() => closeDialog(false)}>
                                        Cancel
                                    </Button>
                                    <Button onClick={submit} className="rounded-sm bg-[#002FA7]" data-testid="partner-submit-btn">
                                        {editId ? "Save changes" : "Create"}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                        </div>
                    ) : null
                }
            />

            <Input
                placeholder="Search partners…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="rounded-sm h-9 max-w-sm mb-3"
                data-testid="partner-search"
            />
            <div className="bg-white border border-slate-200 overflow-hidden">
                <table className="w-full text-[13px]">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            <th className="px-4 py-2.5">Name</th>
                            <th className="px-4 py-2.5">Type</th>
                            <th className="px-4 py-2.5">Country</th>
                            <th className="px-4 py-2.5">Emails</th>
                            <th className="px-4 py-2.5 w-28 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr>
                                <td colSpan={5} className="text-center text-slate-500 py-10">
                                    No partners
                                </td>
                            </tr>
                        )}
                        {filtered.map((p) => (
                            <tr key={p.id} className="border-b border-slate-100" data-testid={`partner-row-${p.id}`}>
                                <td className="px-4 py-2">{p.name}</td>
                                <td className="px-4 py-2">
                                    <span className="text-[11px] uppercase tracking-widest text-slate-600 bg-slate-100 px-2 py-0.5">
                                        {p.type}
                                    </span>
                                </td>
                                <td className="px-4 py-2 text-slate-600">{p.country || "—"}</td>
                                <td className="px-4 py-2">
                                    {(p.emails || []).length === 0 ? (
                                        <span className="text-slate-400 text-[12px]">—</span>
                                    ) : (
                                        <span
                                            title={(p.emails || []).join(", ")}
                                            className="font-mono text-[11.5px] text-slate-700"
                                        >
                                            {p.emails[0]}
                                            {p.emails.length > 1 && (
                                                <span className="ml-1 text-[10.5px] text-slate-500">
                                                    +{p.emails.length - 1}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-right">
                                    {canWrite && (
                                        <>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => startEdit(p)}
                                                className="h-7 px-2"
                                                data-testid={`partner-edit-${p.id}`}
                                            >
                                                <Pencil size={13} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => remove(p.id)}
                                                className="h-7 px-2 text-red-600"
                                                data-testid={`partner-delete-${p.id}`}
                                            >
                                                <Trash2 size={13} />
                                            </Button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
